import type { Mesh } from "@babylonjs/core/Meshes/mesh";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { PBRMaterial } from "@babylonjs/core/Materials/PBR/pbrMaterial";
import type { ShadowGenerator } from "@babylonjs/core/Lights/Shadows/shadowGenerator";
import { CanopyMechanismSkin } from "./CanopyMechanismSkin";
import { tagAs } from "../PlaceholderTag";
import mechanismSource from "../../../assets/models-src/environment/canopy_mechanisms.json";

const CASE_BOTTOM=mechanismSource.assets.find(asset=>asset.file==="canopy_instrument_case.glb")!.bounds.min[1];
const housingLift=(scale:number,floorOffset:number):number=>Math.max(0,floorOffset-CASE_BOTTOM*scale);

/** Match the existing .6m Dial source face to the cast housing and keep its
 * frontmost knob behind the cat lane. The imported conversion root is intact. */
export function canopyDialPlacement(scale=1,floorOffset=-Infinity):{scale:number;offset:Vector3}{
  return{scale:2*scale,offset:new Vector3(0,housingLift(scale,floorOffset),-.72*scale)};
}

/** Cabinet behind the existing Dial; its original skin, pointer, notches,
 * highlight and target remain the sole source of puzzle feedback. */
export class CanopyInstrumentMount {
  readonly ready: Promise<boolean>;
  private readonly skin: CanopyMechanismSkin;
  private readonly stems: Mesh[] = [];
  private readonly material: PBRMaterial;
  private readonly observer;
  private retired = false;
  constructor(private readonly root: Mesh, floorY: number, private readonly shadows: ShadowGenerator,
    scale = 1, footX = 0) {
    const scene = root.getScene();
    this.material = new PBRMaterial(root.name + "_instrumentSupport",scene);
    this.material.albedoColor = new Color3(.07,.085,.075); this.material.roughness = .72; this.material.metallic = .6;
    const bottom = floorY - root.position.y, top = -.1 * scale;
    if (top - bottom > .1) for (const x of [-.3,.3]) {
      const stem = MeshBuilder.CreateTube("canopyInstrumentStem_" + root.name + "_" + x,
        { path:[new Vector3(x+footX,bottom,-.72),new Vector3(x,top,-.72)],radius:.055,tessellation:12 },scene);
      stem.parent = root; stem.material = this.material; stem.isPickable = false; stem.receiveShadows = true;
      tagAs(stem,"modular-final"); this.stems.push(stem); shadows.addShadowCaster(stem,false);
    }
    this.skin = new CanopyMechanismSkin(root,shadows,"instrument",{scale,
      offset:new Vector3(0,housingLift(scale,bottom),0),visible:true,fallbackMeshes:[]});
    this.ready = this.skin.ready;
    this.observer = root.onDisposeObservable.addOnce(()=>this.dispose());
  }
  dispose():void {
    if(this.retired)return;this.retired=true;this.root.onDisposeObservable.remove(this.observer);this.skin.dispose();
    for(const m of this.stems){this.shadows.removeShadowCaster(m,false);m.dispose();}this.material.dispose();
  }
}
